import type { FC } from 'react';
import dayjs from 'dayjs';

import { getEffectiveLegTimes } from '@mopla/business-logic';
import { NormalizedScheduledLeg } from '@mopla/data-models';

import { Row, TripCardRoot } from './DistributorNotificationModal.styles';

interface TripCardProps {
	leg: NormalizedScheduledLeg & { updateState: string };
}

interface ITripTimeProps {
	planned: string;
	estimated: string;
	showDeviation: boolean;
	testId: string;
}

const formatTime = (time?: string): string => dayjs(time).format('HH:mm');

/** Estimated label with the planned one struck through when they differ. */
const TripTime: FC<ITripTimeProps> = ({
	planned,
	estimated,
	showDeviation,
	testId,
}) => {
	if (!showDeviation || planned === estimated) {
		return <span data-testid={`${testId}-text`}>{planned}</span>;
	}

	return (
		<span data-testid={`${testId}-text`}>
			<span className="deviatedTime">{estimated}</span>
			<span className="plannedTime" data-testid={`${testId}Planned-text`}>
				{planned}
			</span>
		</span>
	);
};

export const TripCard: FC<TripCardProps> = ({ leg }) => {
	const getVariant = () => {
		if (leg.updateState === 'DELETED') return 'grey';
		if (leg.state === 'BREAK') return 'blue';
		if (leg.passengers) return 'secondary_dark';
		return 'yellow';
	};

	/** A cancelled ride has no estimate worth showing. */
	const showDeviation = leg.updateState !== 'DELETED';
	const { effectiveStart, effectiveEnd } = getEffectiveLegTimes(leg);

	return (
		<TripCardRoot variant={getVariant()}>
			{leg.state === 'BREAK' ? (
				<Row>
					<TripTime
						planned={`${formatTime(leg.start)} — ${formatTime(leg.end)}`}
						estimated={`${formatTime(effectiveStart)} — ${formatTime(
							effectiveEnd
						)}`}
						showDeviation={showDeviation}
						testId="tripCard-breakTime"
					/>
				</Row>
			) : (
				<>
					<Row variant={getVariant()}>
						<TripTime
							planned={formatTime(leg.start)}
							estimated={formatTime(effectiveStart)}
							showDeviation={showDeviation}
							testId="tripCard-departureTime"
						/>
						<span>{leg?.from?.name}</span>
					</Row>
					<Row variant={getVariant()}>
						<TripTime
							planned={formatTime(leg.end)}
							estimated={formatTime(effectiveEnd)}
							showDeviation={showDeviation}
							testId="tripCard-arrivalTime"
						/>
						<span>{leg?.to?.name}</span>
					</Row>
				</>
			)}
		</TripCardRoot>
	);
};

export default TripCard;
